import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dfed4268-137d-4b89-bcab-122f53e08876/artifacts/k5gcf49q_BBA%20LOGO.webp";

// Custom Logo Component with clean styling
const Logo = ({ className = "h-20 w-auto" }) => (
  <div className={`relative ${className}`} style={{ aspectRatio: '1/1' }}>
    <img 
      src={LOGO_URL} 
      alt="Bodies By Akeem" 
      className="h-full w-auto rounded-full"
      style={{ 
        background: 'transparent',
        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))'
      }}
    />
  </div>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(user.onboarding_completed ? '/dashboard' : '/onboarding');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      toast.success('Welcome back!');
      navigate(userData.onboarding_completed ? '/dashboard' : '/onboarding');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center mb-12">
            <Logo className="h-20" />
          </Link>
          
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl text-white uppercase mb-2">Welcome Back</h1>
            <p className="text-gray-400">Sign in to continue your fitness journey</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-tactical w-full bg-secondary border-transparent"
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-tactical w-full bg-secondary border-transparent pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary focus:ring-primary" />
                <span className="text-sm text-gray-400">Remember me</span>
              </label>
              <a href="#" className="text-sm text-primary hover:text-red-400">Forgot password?</a>
            </div>
            
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold py-6"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          <p className="text-center mt-8 text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary hover:text-red-400 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
      
      {/* Right Side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <img 
          src="https://images.unsplash.com/photo-1628273278105-95b6cb4b8afe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxpbnRlbnNlJTIwZ3ltJTIwd29ya291dCUyMGF0aGxldGUlMjBkYXJrJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwMzk5NTY2fDA&ixlib=rb-4.1.0&q=85"
          alt="Athlete"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0A0A0A]"></div>
      </div>
    </div>
  );
}
